<template>
  <section
    class="gold_top_rule relative flex flex-col gap-1.5 rounded-lg border border-border-strong bg-bg-elevated/70 p-1.5 shadow-md"
    :data-room-id="room.id"
    :data-group-layout="groupsVertical ? 'vertical' : 'horizontal'"
  >
    <div class="flex flex-wrap items-center justify-between gap-2">
      <RoomHeader
        :room="room"
        :mode="mode"
        :can-edit="canEdit || canEditRaidRoom"
        :groups-vertical="groupsVertical"
        :stage="stage"
        @name-commit="onNameCommit"
        @move-up="onMoveUp"
        @move-down="onMoveDown"
        @remove="onRemoveRoom"
        @toggle-group-layout="toggleGroupLayout"
      />
      <div class="flex items-center gap-3">
        <WeaponStatsPopup
          :room="room"
          :role-counts="roleCounts"
          :sorted-stats-rows="sortedStatsRows"
          :stats-role-tiles="statsRoleTiles"
          :stats-total="statsTotal"
          :open="statsPopupOpen"
          @update:open="statsPopupOpen = $event"
        />
      </div>
    </div>

    <div class="min-h-0">
      <div
        class="grid gap-1.5"
        :class="[groupGridClass, groupsVertical ? 'items-start' : 'items-stretch']"
      >
        <PlayerGroup
          v-for="group in groupsForRoom"
          :key="group.id"
          :group="group"
          :mode="mode"
          :players-as-grid="groupsVertical"
          :player-filters-active="playerFiltersActive"
          :player-filter-mode="playerFilterMode"
          :matching-player-ids="matchingPlayerIds"
          @use-build="(id, bi) => emit('use-build', id, bi)"
          @use-guild-build="(id, buildId) => emit('use-guild-build', id, buildId)"
          @add-guild-build="emit('add-guild-build')"
          @edit-guild-build="(id, buildId, slot) => emit('edit-guild-build', id, buildId, slot)"
          @add-build="(id) => emit('add-build', id)"
          @open-player-selector="emit('open-player-selector', $event)"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import PlayerGroup from './PlayerGroup.vue'
import RoomHeader from './RoomHeader.vue'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import WeaponStatsPopup, { type WeaponStatsRoleTile, type WeaponStatsRow } from '../weapons/WeaponStatsPopup.vue'
import { POOL_ROOM_ID, useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import { PlayerFilterMode } from '../../lib/playerFilters'
import type { PlayerFilterMode as PlayerFilterModeType } from '../../lib/playerFilters'
import type { GroupId, Player, Room } from '../../types'
import { BoardMode, ViewMode } from '../../types'
import { WEAPONS_BY_SLUG, weaponIcon as lookupWeaponIcon } from '@/data/weapons'
import { PlayerRole, WeaponRole } from '@/types/weapon'
import { WEAPON_ROLE_ORDER } from '../../lib/roleSort'
import { useRoleColors } from '@/features/gvg-planner/composables'

export interface RoomSectionProps {
  readonly room: Room
  /** Plan 02 — `'live'` (default) lets mutations through; `'raid'` makes the
   *  snapshot read-only (no reorder/remove/rename). */
  readonly mode?: BoardMode
  /** Plan 04 — when non-null, the room header renders a "Stage N" chip so
   *  the per-stage room stack reads as a timeline. */
  readonly stage?: { readonly id: string; readonly kind: string; readonly name: string } | null
  readonly playerFiltersActive?: boolean
  readonly playerFilterMode?: PlayerFilterModeType
  readonly matchingPlayerIds?: ReadonlySet<string>
}

const props = withDefaults(defineProps<RoomSectionProps>(), {
  mode: BoardMode.Live,
  stage: null,
  playerFiltersActive: false,
  playerFilterMode: PlayerFilterMode.Highlight,
  matchingPlayerIds: () => new Set<string>(),
})

const emit = defineEmits<{
  (e: 'use-build', playerId: string, buildIndex: number): void
  (e: 'add-build', playerId: string): void
  (e: 'use-guild-build', playerId: string, buildId: string): void
  (e: 'add-guild-build'): void
  (e: 'edit-guild-build', playerId: string, buildId: string, slot: 0 | 1): void
  (e: 'open-player-selector', groupId: string): void
}>()

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()
const roleColors = useRoleColors()

/**
 * Room layout is a view-only preference. Pool starts stacked because its
 * unbounded buckets benefit from the full board width; every other room keeps
 * the established side-by-side group layout until the user toggles it.
 */
const groupsVertical = ref<boolean>(props.room.id === POOL_ROOM_ID)

/**
 * Compact cards can support more groups per row than detail cards. A vertical
 * room always keeps one group per row; the full-width group then lays its
 * players out as the corresponding responsive card grid.
 */
const groupGridClass = computed<string>(() => {
  if (groupsVertical.value) return 'grid-cols-1'
  return store.viewMode === ViewMode.Compact
    ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
    : 'grid-cols-1 md:grid-cols-2 board:grid-cols-3'
})

function toggleGroupLayout(): void {
  groupsVertical.value = !groupsVertical.value
}

/**
 * Read-only viewers see the board but every mutation bails before
 * dispatching. The literal here is pinned by `lib/__tests__/raids.test.ts`
 * (which searches for `store.isOfficer && props.mode === BoardMode.Live`
 * across `RoomSection` / `PlayerGroup` / `PlayerCard`) so it lives in
 * one canonical place rather than being duplicated in each child.
 */
const canEdit = computed<boolean>(() => store.isOfficer && props.mode === BoardMode.Live)

/**
 * Raid-mode room-move gate. The backend `f_raid_room_move` resolver is
 * officer-only because a room move doesn't have a single snapshot row to
 * gate on. Mirrors that contract so the UI up/down arrows can't fire a
 * mutation the resolver will reject.
 */
const canEditRaidRoom = computed<boolean>(
  () => store.isOfficer && props.mode === BoardMode.Raid && !store.activeRaidEditLocked,
)

const groupsForRoom = computed(() => store.roomGroups(props.room.id))

const groupIdSet = computed<ReadonlySet<GroupId>>(
  () => new Set(groupsForRoom.value.map((g) => g.id)),
)

const playersInRoom = computed<ReadonlyArray<Player>>(() =>
  store.currentPlayers.filter((p) => p.group !== null && groupIdSet.value.has(p.group)),
)

/**
 * Per-role player counts for the room, keyed by `PlayerRole`. Consumed
 * by `WeaponStatsPopup` (trigger chips + role-summary tiles).
 */
const roleCounts = computed<ReadonlyMap<PlayerRole, number>>(() => {
  const counts = new Map<PlayerRole, number>([
    [PlayerRole.Tank, 0],
    [PlayerRole.Dps, 0],
    [PlayerRole.Hybrid, 0],
    [PlayerRole.Heal, 0],
  ])
  for (const p of playersInRoom.value) {
    const r: PlayerRole = p.role
    counts.set(r, (counts.get(r) ?? 0) + 1)
  }
  return counts
})

/** Tile config for the popup's role-summary row. */
const statsRoleTiles = computed<ReadonlyArray<WeaponStatsRoleTile>>(() => [
  {
    role: PlayerRole.Tank,
    label: messages.value.role.statsTank,
    textClass: 'text-role-tank',
    barClass: 'bg-role-tank',
  },
  {
    role: PlayerRole.Dps,
    label: messages.value.role.statsDps,
    textClass: 'text-role-dps',
    barClass: 'bg-role-dps',
  },
  {
    role: PlayerRole.Hybrid,
    label: messages.value.role.statsHybrid,
    textClass: 'text-text-secondary',
    barClass: 'bg-bg-surface',
  },
  {
    role: PlayerRole.Heal,
    label: messages.value.role.statsHealer,
    textClass: 'text-role-heal',
    barClass: 'bg-role-heal',
  },
])

/**
 * Active weapon loadout tally for the room. Sorted by role (Tank → DPS →
 * Heal), count desc, name asc. Bar widths are scaled against the chart's
 * `maxCount` (largest single-weapon count) so the heaviest weapon always
 * renders at 100%. Empty/zero-count rows are filtered (the popup's empty
 * state renders "No weapons assigned" instead). Unknown slugs fall back to
 * the raw slug for the name and the TANK tint so they stay readable.
 */
const sortedStatsRows = computed<ReadonlyArray<WeaponStatsRow>>(() => {
  const counts = store.roomWeaponCounts(props.room.id)
  const rows: WeaponStatsRow[] = []
  for (const [slug, count] of Object.entries(counts)) {
    if (count <= 0) continue
    const weapon = WEAPONS_BY_SLUG.get(slug)
    const role: WeaponRole = weapon?.role ?? WeaponRole.Tank
    rows.push({
      slug,
      name: weapon?.name ?? slug,
      icon: lookupWeaponIcon(slug),
      count,
      barClass: roleColors.forRole(role).bg,
      barWidth: 0,
    })
  }
  const roleRank = new Map<WeaponRole, number>(
    WEAPON_ROLE_ORDER.map((r, i) => [r, i] as const),
  )
  rows.sort((a, b) => {
    const ra = roleRank.get(WEAPONS_BY_SLUG.get(a.slug)?.role ?? WeaponRole.Tank) ?? 0
    const rb = roleRank.get(WEAPONS_BY_SLUG.get(b.slug)?.role ?? WeaponRole.Tank) ?? 0
    if (ra !== rb) return ra - rb
    if (a.count !== b.count) return b.count - a.count
    return a.name.localeCompare(b.name)
  })
  // Scale bar widths against the chart's biggest single-weapon count.
  const maxCount = rows.reduce((m, r) => (r.count > m ? r.count : m), 0)
  if (maxCount > 0) {
    return rows.map((r) => ({ ...r, barWidth: Math.round((r.count / maxCount) * 100) }))
  }
  return rows
})

const statsTotal = computed<number>(() =>
  sortedStatsRows.value.reduce((sum, r) => sum + r.count, 0),
)

// Open flag for the `v-model:open` on `WeaponStatsPopup`. Vue's `v-model`
// shorthand needs a writable target; this ref is it.
const statsPopupOpen = ref<boolean>(false)

// ----- RoomHeader event handlers -----
function onMoveUp(roomId: string): void {
  if (!canEdit.value && !canEditRaidRoom.value) return
  // `currentRooms` is the raid-aware rooms mirror: in raid mode the
  // rendered ids come from `raidRoomToRoom` (snapshot stable ids that
  // never appear in `store.rooms`).
  const idx = store.currentRooms.findIndex((r) => r.id === roomId)
  if (idx <= 0) return
  store.moveRoom(roomId, idx - 1)
}

function onMoveDown(roomId: string): void {
  if (!canEdit.value && !canEditRaidRoom.value) return
  const idx = store.currentRooms.findIndex((r) => r.id === roomId)
  if (idx < 0) return
  store.moveRoom(roomId, idx + 1)
}

function onRemoveRoom(roomId: string): void {
  if (!canEdit.value) return
  // The header confirms and short-circuits on the last-room case; re-check
  // here for any caller that bypasses the header.
  if (store.rooms.length <= 1) return
  store.removeRoom(roomId)
}

function onNameCommit(next: string): void {
  // ISS-20: `renameRoom` intentionally rejects (no `RoomRename` mutation
  // yet) and surfaces the failure through `store.error`. Swallow the
  // rejection here so it doesn't become an unhandled promise rejection;
  // the live-region already shows the store-populated error. The `else`
  // branch is structural so the ISS-20 test can pin "the dispatch is
  // never called on empty/unchanged input"; the contenteditable revert
  // itself is handled in `RoomHeader`.
  if (next && next !== props.room.name) {
    store.renameRoom(props.room.id, next).catch(() => {})
  } else {
    return
  }
}
</script>
